import { useMemo } from 'react';
import { Tooltip } from '@mui/material';

import { DataTable, type TableColumn, EditIcon, TrashIcon } from '@/src/ui';
import { Constants, ReportHeaderStyle, ReportCellStyle } from '@/src/utils';
import type { SchoolBaseDTO } from '../models/schoolDTOs';
import { useAuth } from '@/src/app/context/useAuth';

interface SchoolsTableProps {
    data: Array<SchoolBaseDTO>;
    onEdit: (row: SchoolBaseDTO) => void;
    onDelete: (row: SchoolBaseDTO) => void;
}

export default function SchoolsTable(props: SchoolsTableProps) {
    const { data, onEdit, onDelete } = props;
    const { user } = useAuth();

    const columns = useMemo<TableColumn[]>(() => {
        if (!data || data.length === 0) return [];
        const cols: TableColumn[] = [
            {
                dataKey: 'id',
                header: 'ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => row.id,
            },
            {
                dataKey: 'name',
                header: 'School Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => row.name,
            },
            {
                dataKey: 'code',
                header: 'Code',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => row.code,
            },
            {
                dataKey: 'externalIdentifier',
                header: 'External ID',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => row.externalIdentifier,
            },
            {
                dataKey: 'districtName',
                header: 'District Name',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => row.districtName,
            },
            {
                dataKey: 'enablePracticeOrInherit',
                header: 'Practice',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => {
                    if (row.enablePracticeOrInherit === 1) return 'Enabled';
                    if (row.enablePracticeOrInherit === 0) return 'Disabled';
                    return 'Inherit';
                },
            },
            {
                dataKey: 'enablePracticeChatOrInherit',
                header: 'Practice Chat',
                align: 'left',
                allowSort: true,
                emptyValue: '-',
                headerSx: ReportHeaderStyle,
                cellSx: ReportCellStyle,
                formatter: (row: SchoolBaseDTO) => {
                    if (row.enablePracticeChatOrInherit === 1) return 'Enabled';
                    if (row.enablePracticeChatOrInherit === 0) return 'Disabled';
                    return 'Inherit';
                },
            },
        ];
        cols.push({
            dataKey: 'actions',
            header: 'Actions',
            align: 'center',
            headerSx: ReportHeaderStyle,
            cellSx: ReportCellStyle,
            formatter: (row) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    {onEdit && user?.role === Constants.ROLES.SUPER_ADMIN && (
                        <Tooltip title="Edit" arrow>
                            <button
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                                onClick={() => onEdit(row)}
                            >
                                <span role="img" aria-label="edit">
                                    <EditIcon />
                                </span>
                            </button>
                        </Tooltip>
                    )}
                    {onDelete && user?.role === Constants.ROLES.SUPER_ADMIN && (
                        <Tooltip title="Delete" arrow>
                            <button
                                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                onClick={() => onDelete(row)}
                            >
                                <span role="img" aria-label="delete">
                                    <TrashIcon />
                                </span>
                            </button>
                        </Tooltip>
                    )}
                </div>
            ),
        });
        return cols;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, onEdit, onDelete]);

    return (
        <DataTable
            columns={columns}
            data={data}
            tableName="Schools"
            rowsPerPageInitial={20}
            alternatingRowColor
            defaultSortColumn="name"
            defaultSortDirection="asc"
        />
    );
}
